import React, { useState, useEffect, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Timer, Flame, Trophy, RotateCcw, Check, X, Zap, Star, Brain } from 'lucide-react';

const getCorrectAnswer = (q) => typeof q.answer === 'number' ? q.options[q.answer] : q.answer;

// ========== 1. TIMER CHALLENGE ==========
export function TimerChallengeActivity({ mission, onComplete }) {
  const questions = mission.questions;
  const [currentQ, setCurrentQ] = useState(0);
  const [score, setScore] = useState(0);
  const [selected, setSelected] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [done, setDone] = useState(false);
  const [timeLeft, setTimeLeft] = useState(mission.timeLimit || 75);
  const timerRef = useRef(null);
  const doneRef = useRef(false);

  useEffect(() => {
    setCurrentQ(0); setScore(0); setSelected(null);
    setShowResult(false); setDone(false); doneRef.current = false;
    setTimeLeft(mission.timeLimit || 75);
  }, [mission]);

  useEffect(() => {
    if (done) return;
    timerRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) { clearInterval(timerRef.current); setDone(true); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [done, mission]);

  useEffect(() => {
    if (done && !doneRef.current) {
      doneRef.current = true;
      if (score >= questions.length * 0.6) onComplete({ type: 'timer-challenge', time_left: timeLeft, score, total: questions.length });
    }
  }, [done, score, questions.length, onComplete, timeLeft]);

  const handleAnswer = (answer) => {
    if (showResult || done) return;
    setSelected(answer);
    setShowResult(true);
    if (answer === getCorrectAnswer(questions[currentQ])) setScore(s => s + 1);
    setTimeout(() => {
      if (currentQ + 1 < questions.length) {
        setCurrentQ(c => c + 1); setSelected(null); setShowResult(false);
      } else { clearInterval(timerRef.current); setDone(true); }
    }, 600);
  };

  const restart = () => {
    setCurrentQ(0); setScore(0); setSelected(null);
    setShowResult(false); setDone(false); doneRef.current = false;
    setTimeLeft(mission.timeLimit || 75);
  };

  if (done) {
    const passed = score >= questions.length * 0.6;
    const timeBonus = timeLeft > 0 ? Math.round(timeLeft * 2) : 0;
    return (
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center py-10" data-testid="timer-challenge-result">
        <motion.div animate={{ scale: [1, 1.2, 1] }} transition={{ duration: 0.5 }}
          className={`w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center ${passed ? 'bg-green-500/20' : 'bg-red-500/20'}`}>
          {passed ? <Trophy className="w-10 h-10 text-green-400" /> : <Timer className="w-10 h-10 text-red-400" />}
        </motion.div>
        <h3 className="text-xl font-bold mb-1">{passed ? 'Challenge Complete!' : "Time's Up!"}</h3>
        <p className="text-muted-foreground text-sm mb-2">Score: {score}/{questions.length}</p>
        {timeBonus > 0 && <p className="text-yellow-400 text-sm font-bold mb-2"><Zap className="w-3 h-3 inline" /> Speed Bonus: +{timeBonus} pts</p>}
        <div className="flex items-center justify-center gap-4 mb-6">
          <div className="text-center"><div className="text-lg font-bold text-primary">{timeLeft}s</div><div className="text-xs text-muted-foreground">Time Left</div></div>
          <div className="w-px h-8 bg-border" />
          <div className="text-center"><div className="text-lg font-bold">{Math.round(score / questions.length * 100)}%</div><div className="text-xs text-muted-foreground">Accuracy</div></div>
        </div>
        <Button onClick={restart} data-testid="timer-restart-btn"><RotateCcw className="w-4 h-4 mr-2" /> Try Again</Button>
      </motion.div>
    );
  }

  const q = questions[currentQ];
  const correctAns = getCorrectAnswer(q);
  const totalTime = mission.timeLimit || 75;
  const timerPercent = (timeLeft / totalTime) * 100;
  const timerColor = timerPercent > 50 ? 'bg-green-500' : timerPercent > 25 ? 'bg-yellow-500' : 'bg-red-500';

  return (
    <div data-testid="timer-challenge-activity">
      <div className="flex items-center justify-between mb-4">
        <Badge variant="outline" className="text-xs">Q{currentQ + 1}/{questions.length}</Badge>
        <motion.div className="flex items-center gap-2 px-4 py-2 rounded-full bg-muted/50 border border-border/50"
          animate={timeLeft <= 10 ? { scale: [1, 1.08, 1], borderColor: ['rgba(239,68,68,0.3)', 'rgba(239,68,68,0.8)', 'rgba(239,68,68,0.3)'] } : {}}
          transition={{ repeat: Infinity, duration: 0.6 }}>
          <Timer className={`w-5 h-5 ${timeLeft <= 10 ? 'text-red-400' : 'text-primary'}`} />
          <span className={`text-xl font-mono font-bold ${timeLeft <= 10 ? 'text-red-400' : ''}`}>{timeLeft}s</span>
        </motion.div>
        <Badge variant="secondary" className="text-xs"><Zap className="w-3 h-3 mr-1" />{score}</Badge>
      </div>
      <div className="w-full h-2.5 rounded-full bg-muted/50 mb-6 overflow-hidden">
        <motion.div className={`h-full rounded-full ${timerColor} transition-colors`} animate={{ width: `${timerPercent}%` }} transition={{ duration: 0.5 }} />
      </div>
      <motion.h3 key={currentQ} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="text-base font-semibold mb-6">{q.question}</motion.h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {q.options.map((opt, i) => (
          <motion.button key={opt} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
            onClick={() => handleAnswer(opt)} disabled={showResult}
            className={`px-5 py-4 rounded-xl border text-sm text-left transition-all ${
              showResult && opt === correctAns ? 'border-green-500 bg-green-500/15 text-green-400 scale-[1.02]' :
              showResult && opt === selected && opt !== correctAns ? 'border-red-500 bg-red-500/15 text-red-400' :
              !showResult ? 'border-border/50 bg-card hover:border-primary/40 hover:shadow-sm cursor-pointer active:scale-[0.98]' :
              'border-border/30 bg-card opacity-40'
            }`} data-testid={`timer-option-${i}`}>
            <div className="flex items-center gap-3">
              <span className="w-7 h-7 rounded-full bg-muted/50 flex items-center justify-center text-xs font-bold shrink-0">{String.fromCharCode(65 + i)}</span>
              <span>{opt}</span>
              {showResult && opt === correctAns && <Check className="w-4 h-4 text-green-400 ml-auto" />}
              {showResult && opt === selected && opt !== correctAns && <X className="w-4 h-4 text-red-400 ml-auto" />}
            </div>
          </motion.button>
        ))}
      </div>
    </div>
  );
}

// ========== 2. STREAK COMBO ==========
export function StreakComboActivity({ mission, onComplete }) {
  const questions = useMemo(() => {
    if (mission.questions) return mission.questions;
    if (mission.items && mission.items[0]?.left) {
      return mission.items.map((item, _, arr) => {
        const wrongs = arr.filter(i => i.id !== item.id).map(i => i.right).sort(() => Math.random() - 0.5).slice(0, 3);
        return { question: `What matches "${item.left}"?`, options: [item.right, ...wrongs].sort(() => Math.random() - 0.5), answer: item.right };
      });
    }
    if (mission.items && mission.items[0]?.sentence) {
      const allAnswers = mission.items.map(i => i.answer);
      return mission.items.map(item => {
        const wrongs = [...allAnswers.filter(a => a !== item.answer), ...(mission.distractors || [])].sort(() => Math.random() - 0.5).slice(0, 3);
        return { question: item.sentence.replace('___', '______'), options: [item.answer, ...wrongs].sort(() => Math.random() - 0.5), answer: item.answer };
      });
    }
    return [];
  }, [mission]);

  const [currentQ, setCurrentQ] = useState(0);
  const [score, setScore] = useState(0);
  const [combo, setCombo] = useState(0);
  const [maxCombo, setMaxCombo] = useState(0);
  const [totalPoints, setTotalPoints] = useState(0);
  const [selected, setSelected] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [done, setDone] = useState(false);
  const [comboBreak, setComboBreak] = useState(false);
  const doneRef = useRef(false);

  useEffect(() => {
    setCurrentQ(0); setScore(0); setCombo(0); setMaxCombo(0); setTotalPoints(0);
    setSelected(null); setShowResult(false); setDone(false); doneRef.current = false; setComboBreak(false);
  }, [mission]);

  useEffect(() => {
    if (done && !doneRef.current) {
      doneRef.current = true;
      if (score >= questions.length * 0.6) onComplete({ type: 'streak-combo', max_combo: maxCombo, total_points: totalPoints, score, total: questions.length });
    }
  }, [done, score, questions.length, onComplete, maxCombo, totalPoints]);

  const advance = () => {
    if (currentQ + 1 < questions.length) {
      setCurrentQ(c => c + 1); setSelected(null); setShowResult(false); setComboBreak(false);
    } else { setDone(true); }
  };

  const handleAnswer = (answer) => {
    if (showResult || done) return;
    setSelected(answer);
    setShowResult(true);
    const isCorrect = answer === getCorrectAnswer(questions[currentQ]);
    if (isCorrect) {
      setScore(s => s + 1);
      setCombo(c => {
        const newCombo = c + 1;
        if (newCombo > maxCombo) setMaxCombo(newCombo);
        setTotalPoints(p => p + (10 * Math.min(newCombo, 5)));
        return newCombo;
      });
    } else {
      setCombo(0);
      setComboBreak(true);
    }
    setTimeout(advance, 900);
  };

  const restart = () => {
    setCurrentQ(0); setScore(0); setCombo(0); setMaxCombo(0); setTotalPoints(0);
    setSelected(null); setShowResult(false); setDone(false); doneRef.current = false; setComboBreak(false);
  };

  if (done) {
    const passed = score >= questions.length * 0.6;
    return (
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center py-10" data-testid="streak-combo-result">
        <motion.div animate={{ scale: [1, 1.2, 1] }} transition={{ duration: 0.5 }}
          className={`w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center ${passed ? 'bg-orange-500/20' : 'bg-red-500/20'}`}>
          {passed ? <Flame className="w-10 h-10 text-orange-400" /> : <X className="w-10 h-10 text-red-400" />}
        </motion.div>
        <h3 className="text-xl font-bold mb-1">{passed ? 'Streak Master!' : 'Keep Practicing!'}</h3>
        <p className="text-muted-foreground text-sm mb-4">Score: {score}/{questions.length} | Points: {totalPoints}</p>
        <div className="flex items-center justify-center gap-4 mb-6">
          <div className="text-center"><div className="text-2xl font-bold text-orange-400">{maxCombo}x</div><div className="text-xs text-muted-foreground">Best Combo</div></div>
          <div className="w-px h-8 bg-border" />
          <div className="text-center"><div className="text-lg font-bold text-primary">{totalPoints}</div><div className="text-xs text-muted-foreground">Total Points</div></div>
          <div className="w-px h-8 bg-border" />
          <div className="text-center"><div className="text-lg font-bold">{Math.round(score / questions.length * 100)}%</div><div className="text-xs text-muted-foreground">Accuracy</div></div>
        </div>
        <Button onClick={restart} data-testid="streak-restart-btn"><RotateCcw className="w-4 h-4 mr-2" /> Try Again</Button>
      </motion.div>
    );
  }

  if (!questions.length) return <p className="text-muted-foreground">No questions available.</p>;
  const q = questions[currentQ];
  const correctAns = getCorrectAnswer(q);
  const comboColor = combo >= 5 ? 'text-red-400' : combo >= 3 ? 'text-orange-400' : combo >= 1 ? 'text-yellow-400' : 'text-muted-foreground';

  return (
    <div data-testid="streak-combo-activity">
      <div className="flex items-center justify-between mb-4 p-3 rounded-xl bg-muted/30">
        <Badge variant="outline" className="text-xs">Q{currentQ + 1}/{questions.length}</Badge>
        <AnimatePresence mode="wait">
          <motion.div key={combo} initial={{ scale: 1.5, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
            className={`flex items-center gap-2 text-lg font-bold ${comboColor}`}>
            {combo >= 2 && <Flame className="w-5 h-5" />}
            <span>{combo}x Combo</span>
            {combo >= 3 && <Flame className="w-5 h-5" />}
          </motion.div>
        </AnimatePresence>
        <Badge variant="secondary" className="text-xs"><Star className="w-3 h-3 mr-1" />{totalPoints} pts</Badge>
      </div>
      {comboBreak && (
        <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
          className="text-center text-red-400 text-xs font-bold mb-2">COMBO BREAK!</motion.div>
      )}
      <div className="w-full h-1.5 rounded-full bg-muted/50 mb-6 overflow-hidden">
        <motion.div className="h-full bg-primary rounded-full" animate={{ width: `${(currentQ / questions.length) * 100}%` }} />
      </div>
      <motion.h3 key={currentQ} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="text-base font-semibold mb-6">{q.question}</motion.h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {q.options.map((opt, i) => (
          <motion.button key={opt} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
            onClick={() => handleAnswer(opt)} disabled={showResult}
            className={`px-5 py-4 rounded-xl border text-sm text-left transition-all ${
              showResult && opt === correctAns ? 'border-green-500 bg-green-500/15 text-green-400 scale-[1.02]' :
              showResult && opt === selected && opt !== correctAns ? 'border-red-500 bg-red-500/15 text-red-400' :
              !showResult ? 'border-border/50 bg-card hover:border-primary/40 hover:shadow-sm cursor-pointer active:scale-[0.98]' :
              'border-border/30 bg-card opacity-40'
            }`} data-testid={`streak-option-${i}`}>
            <div className="flex items-center gap-3">
              <span className="w-7 h-7 rounded-full bg-muted/50 flex items-center justify-center text-xs font-bold shrink-0">{String.fromCharCode(65 + i)}</span>
              <span>{opt}</span>
            </div>
          </motion.button>
        ))}
      </div>
    </div>
  );
}

// ========== 3. MEMORY CARD ==========
export function MemoryCardActivity({ mission, onComplete }) {
  const cards = useMemo(() => {
    const items = mission.items.slice(0, 6);
    const c = [];
    items.forEach(item => {
      c.push({ cardId: `${item.id}-L`, pairId: item.id, content: item.left, side: 'left' });
      c.push({ cardId: `${item.id}-R`, pairId: item.id, content: item.right, side: 'right' });
    });
    return c.sort(() => Math.random() - 0.5);
  }, [mission]);

  const [flipped, setFlipped] = useState([]);
  const [matched, setMatched] = useState([]);
  const [turns, setTurns] = useState(0);
  const [checking, setChecking] = useState(false);
  const [done, setDone] = useState(false);
  const doneRef = useRef(false);

  useEffect(() => {
    setFlipped([]); setMatched([]); setTurns(0); setChecking(false); setDone(false); doneRef.current = false;
  }, [mission]);

  useEffect(() => {
    if (matched.length === cards.length && cards.length > 0 && !doneRef.current) {
      doneRef.current = true;
      setDone(true);
      onComplete({ type: 'memory-card', turns, pairs: cards.length / 2 });
    }
  }, [matched.length, cards.length, onComplete, turns]);

  const handleFlip = (cardId) => {
    if (checking || flipped.includes(cardId) || matched.includes(cardId)) return;
    const newFlipped = [...flipped, cardId];
    setFlipped(newFlipped);

    if (newFlipped.length === 2) {
      setTurns(t => t + 1);
      setChecking(true);
      const [first, second] = newFlipped.map(id => cards.find(c => c.cardId === id));
      if (first.pairId === second.pairId && first.side !== second.side) {
        setTimeout(() => {
          setMatched(prev => [...prev, first.cardId, second.cardId]);
          setFlipped([]);
          setChecking(false);
        }, 500);
      } else {
        setTimeout(() => { setFlipped([]); setChecking(false); }, 900);
      }
    }
  };

  const restart = () => {
    setFlipped([]); setMatched([]); setTurns(0); setChecking(false); setDone(false); doneRef.current = false;
  };

  const totalPairs = cards.length / 2;
  const cols = cards.length <= 8 ? 4 : cards.length <= 12 ? 4 : 6;

  return (
    <div data-testid="memory-card-activity">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-muted-foreground">{mission.instruction || 'Find matching pairs by flipping cards!'}</p>
        <div className="flex items-center gap-3">
          <Badge variant="outline" className="text-xs"><Brain className="w-3 h-3 mr-1" />{turns} turns</Badge>
          <Badge variant="secondary" className="text-xs">{matched.length / 2}/{totalPairs} pairs</Badge>
        </div>
      </div>
      {done && (
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
          className="mb-4 p-3 rounded-xl bg-green-500/10 border border-green-500/30 text-center">
          <div className="flex items-center justify-center gap-2">
            <Trophy className="w-5 h-5 text-green-400" />
            <span className="font-bold text-green-400">All Pairs Found in {turns} turns!</span>
          </div>
        </motion.div>
      )}
      <div className={`grid gap-3`} style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}>
        {cards.map(card => {
          const isFlipped = flipped.includes(card.cardId) || matched.includes(card.cardId);
          const isMatched = matched.includes(card.cardId);
          return (
            <motion.div key={card.cardId} layout whileHover={!isFlipped ? { scale: 1.05 } : {}}
              onClick={() => handleFlip(card.cardId)}
              className={`relative aspect-[3/4] rounded-xl border-2 cursor-pointer select-none overflow-hidden transition-all duration-300
                ${isMatched ? 'border-green-500/50 bg-green-500/10' :
                  isFlipped ? 'border-primary/50 bg-primary/5' :
                  'border-border/50 bg-card hover:border-primary/30 hover:shadow-lg'}`}
              data-testid={`memory-card-${card.cardId}`}>
              <AnimatePresence mode="wait">
                {isFlipped ? (
                  <motion.div key="front" initial={{ rotateY: 90 }} animate={{ rotateY: 0 }} exit={{ rotateY: 90 }}
                    transition={{ duration: 0.2 }}
                    className="absolute inset-0 flex flex-col items-center justify-center p-2 text-center">
                    <span className={`text-[10px] font-bold uppercase mb-1 ${card.side === 'left' ? 'text-blue-400' : 'text-purple-400'}`}>
                      {card.side === 'left' ? (mission.leftLabel || 'Term') : (mission.rightLabel || 'Match')}
                    </span>
                    <span className="text-xs font-medium leading-tight">{card.content}</span>
                    {isMatched && <Check className="w-4 h-4 text-green-400 mt-1" />}
                  </motion.div>
                ) : (
                  <motion.div key="back" initial={{ rotateY: -90 }} animate={{ rotateY: 0 }} exit={{ rotateY: -90 }}
                    transition={{ duration: 0.2 }}
                    className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-primary/20 to-primary/5">
                    <span className="text-2xl font-bold text-primary/30">?</span>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          );
        })}
      </div>
      <div className="flex gap-2 mt-5">
        <Button variant="outline" onClick={restart} data-testid="memory-restart-btn"><RotateCcw className="w-4 h-4 mr-2" /> Restart</Button>
      </div>
    </div>
  );
}

// ========== 4. PUZZLE (WORD SCRAMBLE) ==========
export function PuzzleActivity({ mission, onComplete }) {
  const puzzles = useMemo(() =>
    (mission.items || []).map(item => ({
      id: item.id,
      clue: item.sentence.replace('___', '______'),
      answer: item.answer.toUpperCase(),
      scrambled: item.answer.toUpperCase().split('').sort(() => Math.random() - 0.5),
    })), [mission]);

  const [currentP, setCurrentP] = useState(0);
  const [selected, setSelected] = useState([]);
  const [available, setAvailable] = useState([]);
  const [checked, setChecked] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [score, setScore] = useState(0);
  const [done, setDone] = useState(false);
  const doneRef = useRef(false);

  useEffect(() => {
    setCurrentP(0); setScore(0); setDone(false); doneRef.current = false;
    if (puzzles[0]) {
      setSelected([]);
      setAvailable(puzzles[0].scrambled.map((ch, i) => ({ ch, idx: i, used: false })));
      setChecked(false);
      setIsCorrect(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mission]);

  const resetPuzzle = (idx) => {
    if (!puzzles[idx]) return;
    setSelected([]);
    setAvailable(puzzles[idx].scrambled.map((ch, i) => ({ ch, idx: i, used: false })));
    setChecked(false);
    setIsCorrect(false);
  };

  useEffect(() => {
    if (done && !doneRef.current) {
      doneRef.current = true;
      if (score >= puzzles.length * 0.6) onComplete({ type: 'puzzle', score, total: puzzles.length });
    }
  }, [done, score, puzzles.length, onComplete]);

  const selectLetter = (letterObj) => {
    if (checked) return;
    setAvailable(prev => prev.map(a => a.idx === letterObj.idx ? { ...a, used: true } : a));
    setSelected(prev => [...prev, letterObj]);
  };

  const removeLetter = (index) => {
    if (checked) return;
    const removed = selected[index];
    setSelected(prev => prev.filter((_, i) => i !== index));
    setAvailable(prev => prev.map(a => a.idx === removed.idx ? { ...a, used: false } : a));
  };

  const checkAnswer = () => {
    const userAnswer = selected.map(s => s.ch).join('');
    const correct = userAnswer === puzzles[currentP].answer;
    setChecked(true);
    setIsCorrect(correct);
    if (correct) setScore(s => s + 1);
  };

  const nextPuzzle = () => {
    if (currentP + 1 < puzzles.length) {
      const next = currentP + 1;
      setCurrentP(next);
      resetPuzzle(next);
    } else { setDone(true); }
  };

  const restart = () => {
    setCurrentP(0); setScore(0); setDone(false); doneRef.current = false;
    resetPuzzle(0);
  };

  if (done) {
    const passed = score >= puzzles.length * 0.6;
    return (
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center py-10" data-testid="puzzle-result">
        <motion.div animate={{ scale: [1, 1.2, 1] }} transition={{ duration: 0.5 }}
          className={`w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center ${passed ? 'bg-green-500/20' : 'bg-red-500/20'}`}>
          {passed ? <Trophy className="w-10 h-10 text-green-400" /> : <X className="w-10 h-10 text-red-400" />}
        </motion.div>
        <h3 className="text-xl font-bold mb-1">{passed ? 'Puzzle Master!' : 'Keep Trying!'}</h3>
        <p className="text-muted-foreground text-sm mb-4">Solved: {score}/{puzzles.length}</p>
        <Button onClick={restart} data-testid="puzzle-restart-btn"><RotateCcw className="w-4 h-4 mr-2" /> Try Again</Button>
      </motion.div>
    );
  }

  if (!puzzles.length) return <p className="text-muted-foreground">No puzzles available.</p>;
  const puzzle = puzzles[currentP];

  return (
    <div data-testid="puzzle-activity">
      <div className="flex items-center justify-between mb-4">
        <Badge variant="outline" className="text-xs">Puzzle {currentP + 1}/{puzzles.length}</Badge>
        <Badge variant="secondary" className="text-xs">Solved: {score}</Badge>
      </div>
      <div className="p-4 rounded-xl bg-muted/30 border border-border/30 mb-6">
        <p className="text-sm font-medium leading-relaxed">{puzzle.clue}</p>
        <p className="text-xs text-muted-foreground mt-1">Unscramble the letters to find the answer!</p>
      </div>
      {/* Answer area */}
      <div className="flex flex-wrap gap-2 justify-center mb-6 min-h-[52px] p-4 rounded-xl border-2 border-dashed border-border/50 bg-card/50">
        {selected.length === 0 && <span className="text-xs text-muted-foreground/40 self-center">Click letters below to build your answer</span>}
        <AnimatePresence>
          {selected.map((s, i) => (
            <motion.button key={`sel-${i}`} initial={{ scale: 0 }} animate={{ scale: 1 }} exit={{ scale: 0 }}
              onClick={() => removeLetter(i)}
              className={`w-10 h-10 rounded-lg border-2 flex items-center justify-center text-sm font-bold cursor-pointer transition-all
                ${checked && isCorrect ? 'border-green-500 bg-green-500/15 text-green-400' :
                  checked && !isCorrect ? 'border-red-500 bg-red-500/15 text-red-400' :
                  'border-primary/40 bg-primary/10 text-primary hover:bg-primary/20'}`}
              data-testid={`puzzle-selected-${i}`}>
              {s.ch}
            </motion.button>
          ))}
        </AnimatePresence>
      </div>
      {/* Scrambled letters */}
      <div className="flex flex-wrap gap-2 justify-center mb-6">
        {available.map((a) => (
          <motion.button key={`avail-${a.idx}`}
            onClick={() => !a.used && selectLetter(a)}
            className={`w-10 h-10 rounded-lg border flex items-center justify-center text-sm font-bold transition-all
              ${a.used ? 'opacity-20 cursor-default border-border/30' :
                'border-border/50 bg-card hover:border-primary/40 hover:shadow-md cursor-pointer active:scale-95'}`}
            disabled={a.used || checked}
            data-testid={`puzzle-letter-${a.idx}`}>
            {a.ch}
          </motion.button>
        ))}
      </div>
      {checked && !isCorrect && (
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center text-sm text-green-400/80 mb-4">
          Answer: <span className="font-bold">{puzzle.answer}</span>
        </motion.p>
      )}
      <div className="flex gap-2 justify-center">
        {!checked ? (
          <Button onClick={checkAnswer} disabled={selected.length !== puzzle.answer.length} data-testid="puzzle-check-btn">
            <Check className="w-4 h-4 mr-2" /> Check
          </Button>
        ) : (
          <Button onClick={nextPuzzle} data-testid="puzzle-next-btn">
            {currentP + 1 < puzzles.length ? 'Next Puzzle' : 'Finish'} <Zap className="w-4 h-4 ml-2" />
          </Button>
        )}
        {!checked && (
          <Button variant="outline" onClick={() => resetPuzzle(currentP)} data-testid="puzzle-shuffle-btn">
            <RotateCcw className="w-4 h-4 mr-2" /> Shuffle
          </Button>
        )}
      </div>
    </div>
  );
}

// ========== 5. RAPID FIRE ==========
export function RapidFireActivity({ mission, onComplete }) {
  const statements = mission.statements || mission.items || [];
  const [currentIdx, setCurrentIdx] = useState(0);
  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);
  const [bestStreak, setBestStreak] = useState(0);
  const [answered, setAnswered] = useState(null);
  const [done, setDone] = useState(false);
  const [timeLeft, setTimeLeft] = useState(4);
  const [results, setResults] = useState([]);
  const timerRef = useRef(null);
  const doneRef = useRef(false);

  useEffect(() => {
    setCurrentIdx(0); setScore(0); setStreak(0); setBestStreak(0);
    setAnswered(null); setDone(false); doneRef.current = false; setTimeLeft(4); setResults([]);
  }, [mission]);

  useEffect(() => {
    if (done || answered !== null) return;
    timerRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) { clearInterval(timerRef.current); handleAnswer(null); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIdx, done, answered]);

  useEffect(() => {
    if (done && !doneRef.current) {
      doneRef.current = true;
      if (score >= statements.length * 0.6) onComplete({ type: 'rapid-fire', best_streak: bestStreak, score, total: statements.length });
    }
  }, [done, score, statements.length, onComplete, bestStreak]);

  const advance = () => {
    if (currentIdx + 1 < statements.length) {
      setCurrentIdx(c => c + 1); setAnswered(null); setTimeLeft(4);
    } else { setDone(true); }
  };

  const handleAnswer = (answer) => {
    if (answered !== null) return;
    clearInterval(timerRef.current);
    const stmt = statements[currentIdx];
    const isCorrect = answer === stmt.answer;
    setAnswered(answer);
    setResults(prev => [...prev, { correct: isCorrect }]);
    if (isCorrect) {
      setScore(s => s + 1);
      setStreak(s => { const n = s + 1; if (n > bestStreak) setBestStreak(n); return n; });
    } else { setStreak(0); }
    setTimeout(advance, 700);
  };

  const restart = () => {
    setCurrentIdx(0); setScore(0); setStreak(0); setBestStreak(0);
    setAnswered(null); setDone(false); doneRef.current = false; setTimeLeft(4); setResults([]);
  };

  if (done) {
    const passed = score >= statements.length * 0.6;
    return (
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center py-10" data-testid="rapid-fire-result">
        <motion.div animate={{ scale: [1, 1.2, 1] }} transition={{ duration: 0.5 }}
          className={`w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center ${passed ? 'bg-green-500/20' : 'bg-red-500/20'}`}>
          {passed ? <Zap className="w-10 h-10 text-yellow-400" /> : <X className="w-10 h-10 text-red-400" />}
        </motion.div>
        <h3 className="text-xl font-bold mb-1">{passed ? 'Lightning Fast!' : 'Keep Practicing!'}</h3>
        <p className="text-muted-foreground text-sm mb-4">Score: {score}/{statements.length}</p>
        <div className="flex items-center justify-center gap-4 mb-4">
          <div className="text-center"><div className="text-lg font-bold text-orange-400">{bestStreak}</div><div className="text-xs text-muted-foreground">Best Streak</div></div>
          <div className="w-px h-8 bg-border" />
          <div className="text-center"><div className="text-lg font-bold">{Math.round(score / statements.length * 100)}%</div><div className="text-xs text-muted-foreground">Accuracy</div></div>
        </div>
        <div className="flex flex-wrap gap-1 justify-center mb-6">
          {results.map((r, i) => (
            <div key={i} className={`w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold ${r.correct ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
              {r.correct ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
            </div>
          ))}
        </div>
        <Button onClick={restart} data-testid="rapid-fire-restart-btn"><RotateCcw className="w-4 h-4 mr-2" /> Try Again</Button>
      </motion.div>
    );
  }

  if (!statements.length) return <p className="text-muted-foreground">No statements available.</p>;
  const stmt = statements[currentIdx];
  const isCorrect = answered === stmt.answer;
  const timerColor = timeLeft > 2 ? 'text-green-400' : timeLeft > 1 ? 'text-yellow-400' : 'text-red-400';

  return (
    <div data-testid="rapid-fire-activity">
      <div className="flex items-center justify-between mb-4 p-3 rounded-xl bg-muted/30">
        <Badge variant="outline" className="text-xs">{currentIdx + 1}/{statements.length}</Badge>
        {streak >= 2 && (
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="flex items-center gap-1 text-orange-400 text-xs font-bold">
            <Flame className="w-3.5 h-3.5" /> {streak} streak!
          </motion.div>
        )}
        <div className="flex items-center gap-3">
          <motion.div className={`flex items-center gap-1 text-lg font-mono font-bold ${timerColor}`}
            animate={timeLeft <= 2 ? { scale: [1, 1.1, 1] } : {}} transition={{ repeat: Infinity, duration: 0.3 }}>
            <Zap className="w-4 h-4" /> {timeLeft}s
          </motion.div>
          <Badge variant="secondary" className="text-xs">Score: {score}</Badge>
        </div>
      </div>
      <div className="w-full h-2 rounded-full bg-muted/50 mb-8 overflow-hidden">
        <motion.div className="h-full rounded-full bg-yellow-500" animate={{ width: `${(timeLeft / 4) * 100}%` }} transition={{ duration: 0.3 }} />
      </div>
      <motion.div key={currentIdx} initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
        className={`p-8 rounded-2xl border-2 text-center mb-8 transition-colors ${
          answered !== null ? (isCorrect ? 'border-green-500/40 bg-green-500/5' : 'border-red-500/40 bg-red-500/5') : 'border-border/30 bg-card'
        }`}>
        <p className="text-lg font-medium leading-relaxed">{stmt.text}</p>
        {answered !== null && !isCorrect && (
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-xs text-muted-foreground mt-2">
            Answer: <span className="font-semibold text-foreground">{stmt.answer ? 'True' : 'False'}</span>
          </motion.p>
        )}
      </motion.div>
      <div className="flex justify-center gap-6">
        <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
          onClick={() => handleAnswer(true)} disabled={answered !== null}
          className={`flex items-center gap-2 px-8 py-4 rounded-2xl border-2 text-sm font-bold transition-all ${
            answered === true && isCorrect ? 'border-green-500 bg-green-500/20 text-green-400' :
            answered === true && !isCorrect ? 'border-red-500 bg-red-500/20 text-red-400' :
            answered !== null ? 'opacity-40 border-border/30' :
            'border-green-500/30 bg-green-500/5 text-green-400 hover:border-green-500/60 hover:bg-green-500/10 cursor-pointer'
          }`} data-testid="rapid-true-btn">
          <Check className="w-5 h-5" /> TRUE
        </motion.button>
        <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
          onClick={() => handleAnswer(false)} disabled={answered !== null}
          className={`flex items-center gap-2 px-8 py-4 rounded-2xl border-2 text-sm font-bold transition-all ${
            answered === false && isCorrect ? 'border-green-500 bg-green-500/20 text-green-400' :
            answered === false && !isCorrect ? 'border-red-500 bg-red-500/20 text-red-400' :
            answered !== null ? 'opacity-40 border-border/30' :
            'border-red-500/30 bg-red-500/5 text-red-400 hover:border-red-500/60 hover:bg-red-500/10 cursor-pointer'
          }`} data-testid="rapid-false-btn">
          <X className="w-5 h-5" /> FALSE
        </motion.button>
      </div>
    </div>
  );
}
